import { Component } from "@theme/component";

/**
 * @typedef {Object} Options
 * @property {boolean} [childrenOnly] - Only update children
 * @property {(node: Node | undefined) => string|number|undefined} [getNodeKey] - Get node key for matching
 * @property {(oldNode: Node, newNode: Node) => void} [onBeforeUpdate] - Pre-update hook
 * @property {(node: Node) => void} [onAfterUpdate] - Post-update hook
 * @property {(oldNode: Node, newNode: Node) => boolean} [reject] - Reject a node from being morphed
 */

/**
 * The options for the morph
 * @type {Options}
 */
const MORPH_OPTIONS = {
  childrenOnly: true,
  reject(oldNode, newNode) {
    if (newNode.nodeType === Node.TEXT_NODE && newNode.nodeValue?.trim() === "") {
      return true;
    }

    if (
      newNode instanceof HTMLTemplateElement &&
      newNode.shadowRootMode === "open" &&
      oldNode.parentElement &&
      newNode.parentElement &&
      oldNode.parentElement.tagName === newNode.parentElement.tagName &&
      oldNode.parentElement?.shadowRoot != null
    ) {
      // Ignore template elements of components that are already initialized
      return true;
    }

    if (newNode.nodeType === Node.COMMENT_NODE && newNode.nodeValue === "shopify:rendered_by_section_api") {
      // Remove a comment node injected by the Section Rendering API in the Theme Editor
      return true;
    }

    return false;
  },
  onBeforeUpdate(oldNode, newNode) {
    if (oldNode instanceof Element && newNode instanceof Element) {
      const attributes = ["product-grid-view"];

      for (const attribute of attributes) {
        const oldValue = oldNode.getAttribute(attribute);
        const newValue = newNode.getAttribute(attribute);

        if (oldValue && oldValue !== newValue) {
          newNode.setAttribute(attribute, oldValue);
        }
      }

      // Preserve aria-expanded on summary elements within open details
      if (
        oldNode instanceof HTMLElement &&
        newNode instanceof HTMLElement &&
        oldNode.tagName.toLowerCase() === "summary"
      ) {
        const oldDetails = oldNode.closest("details");
        const oldAriaExpanded = oldNode.getAttribute("aria-expanded");

        if (oldDetails?.open && oldAriaExpanded) {
          newNode.setAttribute("aria-expanded", oldAriaExpanded);
        }
      }

      // Preserve is-open class on details elements
      if (
        oldNode instanceof HTMLDetailsElement &&
        newNode instanceof HTMLDetailsElement &&
        oldNode.open &&
        oldNode.classList.contains("is-open")
      ) {
        newNode.classList.add("is-open");
      }

      // Preserve the in-flight lock class across morphs so a multi-step remove
      // (parent + gift reconciliation) keeps pointer-events:none until the
      // whole chain settles.
      if (oldNode.tagName === "CART-ITEMS-COMPONENT" && newNode.tagName === "CART-ITEMS-COMPONENT") {
        if (oldNode.classList.contains("cart-items-disabled")) {
          newNode.classList.add("cart-items-disabled");
        }
      }

      // Special case for elements that need to keep their style
      const elements = ["floating-panel-component"];

      for (const element of elements) {
        const tagName = element.toUpperCase();
        if (oldNode.tagName === tagName && newNode.tagName === tagName) {
          const oldStyle = oldNode.getAttribute("style");

          if (oldStyle) newNode.setAttribute("style", oldStyle);
        }
      }

      // Preserve temporary view transition name
      if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement && oldNode.style.viewTransitionName) {
        newNode.style.viewTransitionName = oldNode.style.viewTransitionName;
      }

      // Lazily applied stylesheets (`media="print" onload="this.media='all'"`) switch themselves
      // to `all` once loaded, but the server markup still says `print`. Copying that back turns
      // the sheet off until it loads again: an unstyled flash on a section's first morph (the
      // cart drawer on the first add). Keep the live href and media when the incoming link loads
      // the same asset.
      if (oldNode instanceof HTMLLinkElement && newNode instanceof HTMLLinkElement && isSameStylesheet(oldNode, newNode)) {
        newNode.setAttribute("href", oldNode.getAttribute("href") ?? "");

        if (newNode.getAttribute("onload")?.includes("this.media")) {
          const media = oldNode.getAttribute("media");
          media == null ? newNode.removeAttribute("media") : newNode.setAttribute("media", media);
        }
      }
    }
  },
  onAfterUpdate(node) {
    if (node instanceof Component) {
      queueMicrotask(() => node.updatedCallback());
    } else if (node instanceof HTMLElement && node.tagName === "MOTION-COMPONENT") {
      // Fallback: motion-component might not be detected as Component instance
      // This can happen when element is created from HTML string before custom element upgrade
      queueMicrotask(() => {
        if (typeof node.updatedCallback === "function") {
          node.updatedCallback();
        }
      });
    } else if (node instanceof HTMLElement && node.hasAttribute("is")) {
      // Handle customized built-in elements (e.g., <img is="responsive-image">)
      const customElementName = node.getAttribute("is");
      if (customElementName && typeof node.updatedCallback === "function") {
        queueMicrotask(() => {
          node.updatedCallback();
        });
      }
    }
  },
};

/**
 * Morphs one DOM tree into another by comparing nodes and applying minimal changes
 * @param {Node} oldTree - The existing DOM tree
 * @param {Node | string} newTree - The new DOM tree to morph to
 * @param {Options} [options] - Configuration options
 * @returns {Node} The morphed DOM tree
 */
export function morph(oldTree, newTree, options = MORPH_OPTIONS) {
  if (!oldTree || !newTree) {
    throw new Error("Both oldTree and newTree must be provided");
  }

  if (typeof newTree === "string") {
    const parsedNewTree = new DOMParser().parseFromString(newTree, "text/html").body.firstChild;
    if (!parsedNewTree) {
      throw new Error("newTree string is not valid HTML");
    }
    newTree = parsedNewTree;
  }

  if (options.childrenOnly) {
    updateChildren(newTree, oldTree, options);
    return oldTree;
  }

  if (newTree.nodeType === 11) {
    throw new Error("newTree should have one root node (not a DocumentFragment)");
  }

  return walk(newTree, oldTree, options);
}

/**
 * Walk and morph a dom tree
 * @param {Node} newNode - The new node to morph to
 * @param {Node} oldNode - The old node to morph from
 * @param {Options} options - The options object
 * @returns {Node} The new node or the morphed old node
 */
function walk(newNode, oldNode, options) {
  // Skip morphing if there is no old or new node
  if (!oldNode) return newNode;
  if (!newNode) return oldNode;

  // Skip morphing if nodes are identical
  if (newNode.isSameNode?.(oldNode)) return oldNode;

  // Check node type and tag name first
  if (newNode.nodeType !== oldNode.nodeType) return newNode;
  if (newNode instanceof Element && oldNode instanceof Element) {
    // Skip morphing if the node is shopify-accelerated-checkout-cart https://shopify.dev/docs/storefronts/themes/pricing-payments/accelerated-checkout#implement-accelerated-checkout-buttons-on-cart
    if (oldNode.tagName === "SHOPIFY-ACCELERATED-CHECKOUT-CART") return oldNode;

    if (newNode.tagName !== oldNode.tagName) return newNode;

    // Only check keys for elements, and only if both nodes have keys
    const newKey = getNodeKey(newNode, options);
    const oldKey = getNodeKey(oldNode, options);
    if (newKey && oldKey && newKey !== oldKey) return newNode;

    // Customized built-in elements: MORPH instead of REPLACE
    // Safari < 16 has bugs with customized built-in elements - replacing causes blur/broken images
    // By morphing (updating attributes on existing element), we keep the already-upgraded element
    // and avoid Safari's broken custom element upgrade for parsed HTML nodes
  }

  // We can morph, update the node and its children
  if (
    oldNode instanceof Element &&
    oldNode.hasAttribute("data-skip-node-update") &&
    newNode instanceof Element &&
    newNode.hasAttribute("data-skip-node-update")
  ) {
    // This is a special case where we don't want to morph the node, but we want to morph the children
    updateChildren(newNode, oldNode, options);
  } else {
    updateNode(newNode, oldNode, options);
    updateChildren(newNode, oldNode, options);
  }

  // Call onAfterUpdate with oldNode (the actual DOM node that was morphed)
  // newNode is just a template from parsed HTML, not the live instance
  options.onAfterUpdate?.(oldNode);

  return oldNode;
}

/**
 * Core morphing function that updates attributes and special elements
 * @param {Node} newNode - Source node with desired state
 * @param {Node} oldNode - Target node to update
 * @param {Options} options - The options object
 */
function updateNode(newNode, oldNode, options) {
  options.onBeforeUpdate?.(oldNode, newNode);

  if (
    (newNode instanceof HTMLDetailsElement && oldNode instanceof HTMLDetailsElement) ||
    (newNode instanceof HTMLDialogElement && oldNode instanceof HTMLDialogElement)
  ) {
    if (!newNode.hasAttribute("declarative-open")) {
      newNode.open = oldNode.open;
    }
  }

  if (oldNode instanceof HTMLElement && newNode instanceof HTMLElement) {
    // Preserve slot attribute
    const oldSlot = oldNode.getAttribute("slot");
    const newSlot = newNode.getAttribute("slot");
    if (oldSlot !== newSlot) {
      oldSlot == null ? newNode.removeAttribute("slot") : newNode.setAttribute("slot", oldSlot);
    }

    // Preserve sizes attribute ONLY for non-responsive-image elements
    // responsive-image in JS mode will recalculate sizes dynamically
    const isResponsiveImage = newNode.getAttribute("is") === "responsive-image";
    const isJSMode = isResponsiveImage && newNode.dataset.mode === "js";

    if (!isJSMode) {
      const oldSizes = oldNode.getAttribute("sizes");
      const newSizes = newNode.getAttribute("sizes");
      if (oldSizes !== newSizes) {
        oldSizes == null ? newNode.removeAttribute("sizes") : newNode.setAttribute("sizes", oldSizes);
      }
    }

    // A responsive image whose source is in `data-srcset` (JS mode, and liquid mode below the
    // first screen) — and the <source> of a <picture> wrapping one — gets `srcset`/`src` from the
    // image scheduler at runtime, so server markup never carries them.
    // Copying that absence onto a hydrated image strips its source: the browser drops the frame
    // it had decoded and paints an empty box until `updatedCallback` puts the attribute back and
    // it decodes again. That is a flash on every morph, most obvious on large artwork that did
    // not even change — variant tiles re-rendering on a variant switch, say. Carry the live
    // values onto the incoming node so `copyAttributes` finds nothing to change and the image is
    // never disturbed; `sizes` rides along because re-deriving it restarts source selection for
    // the same reason. Skipped when the `data-*` sources differ, which is a genuinely different
    // picture that should load.
    const hydratesOwnSource =
      (isResponsiveImage || newNode instanceof HTMLSourceElement) && (newNode.dataset.srcset != null || newNode.dataset.src != null);

    if (
      hydratesOwnSource &&
      oldNode.dataset.srcset === newNode.dataset.srcset &&
      oldNode.dataset.src === newNode.dataset.src
    ) {
      for (const attribute of ["srcset", "src", "sizes"]) {
        const value = oldNode.getAttribute(attribute);
        if (value) newNode.setAttribute(attribute, value);
      }
    }
  }

  if (newNode instanceof Element && oldNode instanceof Element) {
    if (!oldNode.isEqualNode(newNode)) {
      copyAttributes(newNode, oldNode);
    }
  } else if (newNode instanceof Text || newNode instanceof Comment) {
    if (oldNode.nodeValue !== newNode.nodeValue) {
      oldNode.nodeValue = newNode.nodeValue;
    }
  }

  // Handle special elements
  if (newNode instanceof HTMLInputElement && oldNode instanceof HTMLInputElement) {
    updateInput(newNode, oldNode);
  } else if (newNode instanceof HTMLOptionElement && oldNode instanceof HTMLOptionElement) {
    updateAttribute(newNode, oldNode, "selected");
  } else if (newNode instanceof HTMLTextAreaElement && oldNode instanceof HTMLTextAreaElement) {
    updateTextarea(newNode, oldNode);
  }
}

/**
 * Gets a node's key using the getNodeKey option if provided
 * @param {Node | undefined} node - The node to get the key from
 * @param {Options} [options] - The options object that may contain getNodeKey
 * @returns {string|number|undefined} The node's key if one exists
 */
function getNodeKey(node, options) {
  return options?.getNodeKey?.(node) ?? (node instanceof Element ? node.id : undefined);
}

/**
 * Updates a boolean attribute and its corresponding property on an element
 * @param {any} newNode - The new element
 * @param {any} oldNode - The existing element to update
 * @param {string} name - The name of the attribute/property to update
 */
function updateAttribute(newNode, oldNode, name) {
  if (newNode[name] !== oldNode[name]) {
    oldNode[name] = newNode[name];
    if (newNode[name] != null) {
      oldNode.setAttribute(name, "");
    } else {
      oldNode.removeAttribute(name);
    }
  }
}

/**
 * Copies attributes from a new node to an old node, handling namespaced attributes
 * @param {Element} newNode - The new node to copy attributes from
 * @param {Element} oldNode - The existing node to update attributes on
 */
function copyAttributes(newNode, oldNode) {
  const oldAttrs = oldNode.attributes;
  const newAttrs = newNode.attributes;

  // Update or add new attributes
  for (const attr of Array.from(newAttrs)) {
    const { name: attrName, namespaceURI: attrNamespaceURI, value: attrValue } = attr;
    const localName = attr.localName || attrName;

    if (attrName === "src" || attrName === "href" || attrName === "srcset" || attrName === "poster") {
      // Skip updating resource attributes when the value hasn't changed
      // to prevent unnecessary network requests
      if (oldNode.getAttribute(attrName) === attrValue) continue;
    }

    if (attrNamespaceURI) {
      const fromValue = oldNode.getAttributeNS(attrNamespaceURI, localName);
      if (fromValue !== attrValue) {
        oldNode.setAttributeNS(attrNamespaceURI, localName, attrValue);
      }
    } else {
      if (!oldNode.hasAttribute(attrName)) {
        oldNode.setAttribute(attrName, attrValue);
      } else {
        const fromValue = oldNode.getAttribute(attrName);
        if (fromValue !== attrValue) {
          if (attrValue === "null" || attrValue === "undefined") {
            oldNode.removeAttribute(attrName);
          } else {
            oldNode.setAttribute(attrName, attrValue);
          }
        }
      }
    }
  }

  // Remove old attributes not present in new node
  for (const attr of Array.from(oldAttrs)) {
    if (attr.specified === false) continue;

    const { name: attrName, namespaceURI: attrNamespaceURI } = attr;
    const localName = attr.localName || attrName;

    if (attrNamespaceURI) {
      if (!newNode.hasAttributeNS(attrNamespaceURI, localName)) {
        oldNode.removeAttributeNS(attrNamespaceURI, localName);
      }
    } else if (!newNode.hasAttribute(attrName)) {
      oldNode.removeAttribute(attrName);
    }
  }
}

/**
 * Updates special properties and attributes on input elements
 * Handles checked, disabled, indeterminate states and value
 * @param {HTMLInputElement} newNode - The new input element
 * @param {HTMLInputElement} oldNode - The existing input element to update
 */
function updateInput(newNode, oldNode) {
  const newValue = newNode.value;

  updateAttribute(newNode, oldNode, "checked");
  updateAttribute(newNode, oldNode, "disabled");

  // Handle indeterminate state (cannot be set via HTML attribute)
  if (newNode.indeterminate !== oldNode.indeterminate) {
    oldNode.indeterminate = newNode.indeterminate;
  }

  // Skip file inputs since they can't be changed programmatically
  if (oldNode.type === "file") return;

  if (newValue !== oldNode.value) {
    oldNode.setAttribute("value", newValue);
    oldNode.value = newValue;
  }

  if (newValue === "null") {
    oldNode.value = "";
    oldNode.removeAttribute("value");
  }

  if (!newNode.hasAttributeNS(null, "value")) {
    oldNode.removeAttribute("value");
  } else if (oldNode.type === "range") {
    // Update range input UI
    oldNode.value = newValue;
  }
}

/**
 * Updates the value of a textarea element
 * @param {HTMLTextAreaElement} newNode - The new textarea element
 * @param {HTMLTextAreaElement} oldNode - The existing textarea element to update
 */
function updateTextarea(newNode, oldNode) {
  const newValue = newNode.value;
  if (newValue !== oldNode.value) {
    oldNode.value = newValue;
  }

  const firstChild = oldNode.firstChild;
  if (firstChild?.nodeType === Node.TEXT_NODE) {
    if (newValue === "" && firstChild.nodeValue === oldNode.placeholder) {
      return;
    }
    firstChild.nodeValue = newValue;
  }
}

/**
 * Update the children of elements
 * @param {Node} newNode - The new node to update children on
 * @param {Node} oldNode - The existing node to update children on
 * @param {Options} options - The options object
 */
function updateChildren(newNode, oldNode, options) {
  if (
    oldNode instanceof Element &&
    oldNode.hasAttribute("data-skip-subtree-update") &&
    newNode instanceof Element &&
    newNode.hasAttribute("data-skip-subtree-update")
  ) {
    return;
  }

  // DOMParser runs with scripting disabled, so it parses `<noscript>` fallbacks into live
  // elements. Copying them across would add a duplicate `<link rel="stylesheet">` for every
  // lazily loaded sheet; the fallback is inert while scripts run, so leave it as it is.
  if (oldNode.nodeName === "NOSCRIPT") return;

  let oldChild, newChild, morphed, oldMatch;
  let offset = 0;

  for (let i = 0; ; i++) {
    oldChild = oldNode.childNodes[i];
    newChild = newNode.childNodes[i - offset];

    // Both nodes are empty, do nothing
    if (!oldChild && !newChild) {
      break;
    }

    // There is no new child, remove old
    if (!newChild) {
      oldChild && oldNode.removeChild(oldChild);
      i--;
      continue;
    }

    // There is no old child, add new
    if (!oldChild) {
      oldNode.appendChild(newChild);
      // Trigger onAfterUpdate for newly appended custom elements
      if (newChild instanceof Component) {
        // Store reference to avoid closure issues in queueMicrotask
        const component = newChild;
        queueMicrotask(() => {
          if (component && typeof component.updatedCallback === "function") {
            component.updatedCallback();
          }
        });
      }
      offset++;
      continue;
    }

    // Both nodes are the same, morph
    if (same(newChild, oldChild, options)) {
      morphed = walk(newChild, oldChild, options);
      if (morphed !== oldChild) {
        oldNode.replaceChild(morphed, oldChild);
        offset++;
      }
      continue;
    }

    if (options.reject?.(oldChild, newChild)) {
      newNode.removeChild(newChild);
      i--;
      continue;
    }

    // Try to find a matching node to reorder
    oldMatch = null;
    for (let j = i; j < oldNode.childNodes.length; j++) {
      const potentialOldNode = oldNode.childNodes[j];

      if (potentialOldNode && same(potentialOldNode, newChild, options)) {
        oldMatch = potentialOldNode;
        break;
      }
    }

    if (oldMatch) {
      morphed = walk(newChild, oldMatch, options);
      if (morphed !== oldMatch) offset++;
      oldNode.insertBefore(morphed, oldChild);
    } else if (!getNodeKey(newChild, options) && !getNodeKey(oldChild, options)) {
      // Safe to morph in-place if neither has a key
      morphed = walk(newChild, oldChild, options);
      if (morphed !== oldChild) {
        oldNode.replaceChild(morphed, oldChild);
        // If replaced with new node, trigger onAfterUpdate
        if (morphed instanceof Component && morphed !== oldChild) {
          queueMicrotask(() => morphed.updatedCallback?.());
        }
        offset++;
      }
    } else {
      // Insert the node if we couldn't morph or find a match
      oldNode.insertBefore(newChild, oldChild);
      // Trigger onAfterUpdate for newly inserted custom elements
      if (newChild instanceof Component) {
        queueMicrotask(() => newChild.updatedCallback?.());
      }
      offset++;
    }
  }
}

/**
 * Whether two `<link rel="stylesheet">` elements load the same asset. Hosts are ignored: under
 * `shopify theme dev`, cart responses name the shop's myshopify host for assets the page loaded
 * through the local proxy.
 * @param {HTMLLinkElement} a - The first link
 * @param {HTMLLinkElement} b - The second link
 * @returns {boolean} True if both load the same stylesheet
 */
function isSameStylesheet(a, b) {
  if (a.rel !== "stylesheet" || b.rel !== "stylesheet") return false;

  try {
    const aUrl = new URL(a.getAttribute("href") ?? "", document.baseURI);
    const bUrl = new URL(b.getAttribute("href") ?? "", document.baseURI);
    return aUrl.pathname === bUrl.pathname && aUrl.search === bUrl.search;
  } catch {
    return false;
  }
}

/**
 * Check if two nodes are the same
 * @param {Node} a - The first node
 * @param {Node} b - The second node
 * @param {Options} options - The options object
 * @returns {boolean} True if the nodes are the same, false otherwise
 */
function same(a, b, options) {
  // If node types don't match, they're not the same
  if (a.nodeType !== b.nodeType) return false;

  // For elements, check tag name first
  if (a.nodeType === Node.ELEMENT_NODE) {
    if (a instanceof Element && b instanceof Element && a.tagName !== b.tagName) return false;

    // Only compare keys if both nodes have them
    const aKey = getNodeKey(a, options);
    const bKey = getNodeKey(b, options);
    if (aKey && bKey && aKey !== bKey) return false;
  }

  // For text/comment nodes, compare content
  if (a.nodeType === Node.TEXT_NODE && b.nodeType === Node.TEXT_NODE)
    // Trim whitespace to avoid false negatives
    return a.nodeValue?.trim() === b.nodeValue?.trim();
  if (a.nodeType === Node.COMMENT_NODE && b.nodeType === Node.COMMENT_NODE) return a.nodeValue === b.nodeValue;

  // If we get here and nodes are elements with same tag (and compatible keys), they're the same
  return true;
}
